import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { normalizarTexto } from '../../utils/texto';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Search, Check, AlertCircle, ArrowLeft, LayoutTemplate, FileText } from 'lucide-react';
import { createPortal } from 'react-dom';
import axiosInstance from '../../utils/axiosConfig';
import { iniciales } from './utils/helpers';
import { COLORES } from './utils/constants';
import PlantillaActividadesPaso from './plantillas/PlantillaActividadesPaso';
import { duracionLegible, hoyISO } from './plantillas/utils';

// Actividad de plantilla -> fila editable del paso 2.
const aFila = (a) => ({
  orden: a.orden,
  titulo: a.titulo,
  descripcion: a.descripcion,
  asignado_id: a.responsable_empleado_id,
  responsable_texto: a.responsable_texto,
  duracion_dias: a.duracion_dias,
  offset_dias: a.offset_dias,
  prioridad: a.prioridad,
  predecesoras: a.predecesoras || [],
  hito: a.hito,
  incluir: true,
});

const NewProjectModal = ({ isOpen, onClose, onCreated, nombreInicial = '', subtitulo }) => {
  const [nombre, setNombre] = useState('');
  const [codigo, setCodigo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [color, setColor] = useState(COLORES[0].hex);
  const [esContinuo, setEsContinuo] = useState(false);
  const [miembrosSelected, setMiembrosSelected] = useState([]); // [{id, nombre, foto}]
  const [empleados, setEmpleados] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [loadingEmpleados, setLoadingEmpleados] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Punto de partida: en blanco o desde plantilla (tipo de proyecto).
  const [modo, setModo] = useState('blanco');
  const [paso, setPaso] = useState(1);
  const [plantillas, setPlantillas] = useState([]);
  const [areaSel, setAreaSel] = useState(null);
  const [plantillaSel, setPlantillaSel] = useState(null); // detalle con actividades
  const [cargandoPlantilla, setCargandoPlantilla] = useState(false);
  const [fechaInicio, setFechaInicio] = useState(hoyISO());
  const [actividades, setActividades] = useState([]);

  // Reset al cerrar / prellenar al abrir
  useEffect(() => {
    if (!isOpen) {
      setNombre(''); setCodigo(''); setDescripcion(''); setColor(COLORES[0].hex); setEsContinuo(false);
      setMiembrosSelected([]); setBusqueda(''); setError(''); setSubmitting(false);
      setModo('blanco'); setPaso(1); setAreaSel(null); setPlantillaSel(null);
      setFechaInicio(hoyISO()); setActividades([]);
    } else if (nombreInicial) {
      setNombre(nombreInicial.slice(0, 120));
    }
  }, [isOpen, nombreInicial]);

  // Plantillas disponibles (solo activas)
  useEffect(() => {
    if (!isOpen) return;
    axiosInstance.get('/api/proyecto-plantillas')
      .then(({ data }) => setPlantillas(data?.plantillas || []))
      .catch(() => setPlantillas([]));
  }, [isOpen]);

  const areas = useMemo(() => {
    const set = new Set(plantillas.map((p) => p.area || 'General'));
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'es'));
  }, [plantillas]);

  const plantillasDelArea = useMemo(
    () => plantillas.filter((p) => !areaSel || (p.area || 'General') === areaSel),
    [plantillas, areaSel]
  );

  const elegirPlantilla = useCallback(async (p) => {
    setError('');
    setCargandoPlantilla(true);
    try {
      const { data } = await axiosInstance.get(`/api/proyecto-plantillas/${p.id}`);
      setPlantillaSel(data);
      setActividades((data.actividades || []).map(aFila));
    } catch (err) {
      setError('No se pudo cargar la plantilla.');
    } finally {
      setCargandoPlantilla(false);
    }
  }, []);

  // Cargar empleados al abrir
  useEffect(() => {
    if (!isOpen) return;
    setLoadingEmpleados(true);
    axiosInstance.get('/api/empleados')
      .then(({ data }) => {
        setEmpleados(
          (data || []).map((e) => ({
            id: e.id,
            nombre: e.nombre || e.nombre_completo,
            foto: e.foto,
            area: e.area_nombre || e.cargo || '',
            cargo: e.cargo || '',
          }))
        );
      })
      .catch((err) => console.error('Error cargando empleados:', err))
      .finally(() => setLoadingEmpleados(false));
  }, [isOpen]);

  // Close on Escape (en el paso 2, Escape vuelve al paso 1)
  useEffect(() => {
    if (!isOpen) return;
    // Captura + stopPropagation: si el modal se abre encima del slide-over de
    // una tarea, Esc no debe cerrarlo tambien. El resto de las teclas se frena
    // en el onKeyDown del overlay (atajos C / M / flechas del slide-over).
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (paso === 2) setPaso(1);
      else onClose();
    };
    const overflowPrevio = document.body.style.overflow;
    document.addEventListener('keydown', onKey, true);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.body.style.overflow = overflowPrevio;
    };
  }, [isOpen, onClose, paso]);

  const empleadosFiltrados = useMemo(() => {
    const q = normalizarTexto(busqueda).trim();
    // Sin búsqueda: primeros 10 (preview). Con búsqueda: hasta 50 resultados.
    if (!q) return empleados.slice(0, 10);
    return empleados
      .filter((e) => normalizarTexto(e.nombre).includes(q))
      .slice(0, 50);
  }, [empleados, busqueda]);

  const toggleMiembro = (emp) => {
    setMiembrosSelected((prev) =>
      prev.some((m) => m.id === emp.id)
        ? prev.filter((m) => m.id !== emp.id)
        : [...prev, emp]
    );
  };

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    const trimmed = nombre.trim();
    if (!trimmed) {
      setError('Ponle un nombre al proyecto');
      return;
    }
    const conPlantilla = modo === 'plantilla' && plantillaSel;
    if (modo === 'plantilla' && !plantillaSel) {
      setError('Elige una plantilla o cambia a "En blanco".');
      return;
    }
    // Con plantilla, el primer submit lleva a revisar las actividades.
    if (conPlantilla && paso === 1) {
      setPaso(2);
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axiosInstance.post('/api/proyectos', {
        nombre: trimmed,
        ...(codigo ? { slug: codigo } : {}),
        descripcion: descripcion.trim() || null,
        color,
        es_continuo: esContinuo,
        miembros_ids: miembrosSelected.map((m) => m.id),
        ...(conPlantilla ? {
          plantilla: {
            plantilla_id: plantillaSel.id,
            fecha_inicio: fechaInicio,
            actividades: actividades.map(({ _predTexto, nueva, ...a }) => a),
          },
        } : {}),
      });
      onCreated?.(data);
    } catch (err) {
      console.error('Error creando proyecto:', err);
      setError(err.response?.data?.error || 'No pudimos crear el proyecto. Intenta de nuevo.');
      setSubmitting(false);
    }
  }, [nombre, codigo, descripcion, color, esContinuo, miembrosSelected, onCreated, modo, plantillaSel, paso, fechaInicio, actividades]);

  const activasCount = actividades.filter((a) => a.incluir && (a.titulo || '').trim()).length;

  if (!isOpen) return null;

  const modal = (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <motion.div
          initial={{ opacity: 0, y: 24, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 16, scale: 0.96 }}
          transition={{ type: 'spring', stiffness: 380, damping: 28 }}
          layout
          className={`w-full ${paso === 2 ? 'max-w-5xl' : 'max-w-lg'} max-h-[92vh] flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden relative`}
        >
          {/* Acento superior */}
          <div
            className="h-1 w-full flex-shrink-0 transition-colors duration-200"
            style={{ backgroundColor: color }}
          />

          <form onSubmit={handleSubmit} className="flex flex-col min-h-0">
            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-5 pb-4">
              <div className="flex items-start gap-2 min-w-0">
                {paso === 2 && (
                  <button
                    type="button"
                    onClick={() => setPaso(1)}
                    className="mt-0.5 p-1 -ml-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
                    aria-label="Volver a los datos del proyecto"
                  >
                    <ArrowLeft size={15} />
                  </button>
                )}
                <div className="min-w-0">
                  <h2 className="text-base font-semibold tracking-tight text-slate-900 truncate">
                    {paso === 2 ? (nombre.trim() || 'Nuevo proyecto') : 'Nuevo proyecto'}
                  </h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {paso === 2
                      ? `Actividades de "${plantillaSel?.nombre}". Ajusta lo que este proyecto necesita.`
                      : (subtitulo || 'Empieza a organizar el trabajo de tu equipo.')}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-slate-400 hover:text-slate-700 hover:bg-slate-100 p-1.5 rounded-md transition-colors"
                aria-label="Cerrar"
              >
                <X size={16} />
              </button>
            </div>

            {paso === 2 && plantillaSel ? (
              <PlantillaActividadesPaso
                plantillaId={plantillaSel.id}
                actividades={actividades}
                setActividades={setActividades}
                fechaInicio={fechaInicio}
                empleados={empleados}
                color={color}
              />
            ) : (
            <div className="px-6 pb-5 space-y-5 overflow-y-auto min-h-0">
              {/* Punto de partida: en blanco o desde plantilla */}
              {plantillas.length > 0 && (
                <div>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { id: 'blanco', label: 'En blanco', hint: 'Creas las tareas tú', Icon: FileText },
                      { id: 'plantilla', label: 'Desde plantilla', hint: 'Precarga las actividades del tipo de proyecto', Icon: LayoutTemplate },
                    ].map(({ id, label, hint, Icon }) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setModo(id)}
                        aria-pressed={modo === id}
                        className={`flex items-start gap-2.5 px-3 py-2.5 rounded-lg border text-left transition-colors ${
                          modo === id
                            ? 'border-cyan-500 bg-cyan-50/60 ring-2 ring-cyan-500/15'
                            : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        <Icon size={15} className={`mt-0.5 flex-shrink-0 ${modo === id ? 'text-cyan-700' : 'text-slate-400'}`} />
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-slate-800">{label}</span>
                          <span className="block text-[11px] leading-snug text-slate-500">{hint}</span>
                        </span>
                      </button>
                    ))}
                  </div>

                  {modo === 'plantilla' && (
                    <div className="mt-3 space-y-2.5">
                      {areas.length > 1 && (
                        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Área">
                          {[null, ...areas].map((a) => (
                            <button
                              key={a || 'todas'}
                              type="button"
                              onClick={() => setAreaSel(a)}
                              className={`px-2 py-0.5 rounded-md text-xs transition-colors ${
                                areaSel === a
                                  ? 'bg-slate-900 text-white'
                                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                              }`}
                            >
                              {a || 'Todas las áreas'}
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="max-h-44 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
                        {plantillasDelArea.map((p) => {
                          const sel = plantillaSel?.id === p.id;
                          return (
                            <button
                              key={p.id}
                              type="button"
                              onClick={() => elegirPlantilla(p)}
                              className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                                sel ? 'bg-cyan-50' : 'hover:bg-slate-50'
                              }`}
                            >
                              <span className="flex-1 min-w-0">
                                <span className="block text-sm text-slate-800 truncate">{p.nombre}</span>
                                <span className="block text-[11px] text-slate-500">
                                  {p.actividades_count} actividades, {duracionLegible(p.duracion_total_dias)}
                                  {!areaSel && p.area ? ` · ${p.area}` : ''}
                                </span>
                              </span>
                              {sel && <Check size={14} className="text-cyan-600 flex-shrink-0" />}
                            </button>
                          );
                        })}
                      </div>
                      <div className="flex items-center gap-2">
                        <label htmlFor="np-fecha-inicio" className="text-xs font-medium text-slate-600">
                          Arranca el
                        </label>
                        <input
                          id="np-fecha-inicio"
                          type="date"
                          value={fechaInicio}
                          onChange={(e) => setFechaInicio(e.target.value || hoyISO())}
                          className="px-2 py-1 text-sm bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500"
                        />
                        {cargandoPlantilla && <span className="text-[11px] text-slate-400">Cargando actividades...</span>}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Nombre + codigo (prefijo de las tareas, opcional) */}
              <div className="flex gap-2">
                <div className="flex-1 min-w-0">
                  <label htmlFor="np-nombre" className="block text-xs font-medium text-slate-600 mb-1.5">
                    Nombre <span className="text-rose-500">*</span>
                  </label>
                  <input
                    id="np-nombre"
                    type="text"
                    value={nombre}
                    onChange={(e) => setNombre(e.target.value)}
                    placeholder="Ej: Campaña Dalan Q2 2026"
                    maxLength={120}
                    autoFocus
                    className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-md placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition-colors"
                  />
                </div>
                <div className="w-28 flex-shrink-0">
                  <label htmlFor="np-codigo" className="block text-xs font-medium text-slate-600 mb-1.5" title="Prefijo de las tareas: DTSS-1, DTSS-2...">
                    Código
                  </label>
                  <input
                    id="np-codigo"
                    type="text"
                    value={codigo}
                    onChange={(e) => setCodigo(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12))}
                    placeholder="Auto"
                    title="Prefijo de las tareas: DTSS-1, DTSS-2... Si lo dejas vacío se genera del nombre."
                    className="w-full px-3 py-2 text-sm font-mono tracking-wider uppercase bg-white border border-slate-300 rounded-md placeholder-slate-400 placeholder:font-sans placeholder:tracking-normal placeholder:normal-case focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition-colors"
                  />
                </div>
              </div>

              {/* Descripcion */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1.5">
                  Descripción <span className="text-slate-400">(opcional)</span>
                </label>
                <textarea
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  placeholder="¿De qué trata este proyecto?"
                  rows={2}
                  className="w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-md placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 transition-colors resize-none"
                />
              </div>

              {/* Color */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-2">
                  Color de identidad
                </label>
                <div className="flex items-center gap-2">
                  {COLORES.map((c) => (
                    <button
                      key={c.hex}
                      type="button"
                      onClick={() => setColor(c.hex)}
                      title={c.nombre}
                      className="relative w-7 h-7 rounded-full transition-transform hover:scale-110 active:scale-95"
                      style={{ backgroundColor: c.hex }}
                    >
                      {color === c.hex && (
                        <motion.span
                          layoutId="color-selected-ring"
                          className="absolute inset-0 rounded-full ring-2 ring-offset-2 ring-slate-900"
                          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                        />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tipo: proyecto continuo */}
              <div>
                <label className="inline-flex items-center gap-2 cursor-pointer group select-none">
                  <input
                    type="checkbox"
                    checked={esContinuo}
                    onChange={(e) => setEsContinuo(e.target.checked)}
                    className="h-4 w-4 rounded border-slate-300 accent-cyan-600 cursor-pointer"
                  />
                  <span className="text-xs font-medium text-slate-700 group-hover:text-slate-900 transition-colors">
                    Proyecto continuo
                  </span>
                </label>
              </div>

              {/* Miembros */}
              <div>
                <div className="flex items-baseline justify-between mb-1.5">
                  <label className="block text-xs font-medium text-slate-600">
                    Miembros iniciales
                  </label>
                  <span className="text-[11px] text-slate-400">
                    {miembrosSelected.length === 0
                      ? 'Solo tú serás miembro'
                      : `${miembrosSelected.length} ${miembrosSelected.length === 1 ? 'seleccionado' : 'seleccionados'}`}
                  </span>
                </div>

                {/* Selected pills (avatar y texto usan el color del proyecto seleccionado) */}
                {miembrosSelected.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {miembrosSelected.map((m) => (
                      <span
                        key={m.id}
                        className="inline-flex items-center gap-1.5 pl-1 pr-2 py-0.5 rounded-full text-xs"
                        style={{ backgroundColor: `${color}1f`, color }}
                      >
                        <span
                          className="w-4 h-4 rounded-full text-[9px] font-semibold flex items-center justify-center text-white"
                          style={{ backgroundColor: color }}
                        >
                          {iniciales(m.nombre)}
                        </span>
                        {m.nombre.split(' ')[0]}
                        <button
                          type="button"
                          onClick={() => toggleMiembro(m)}
                          className="opacity-60 hover:opacity-100"
                          aria-label="Quitar"
                        >
                          <X size={11} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Buscador */}
                <div className="relative">
                  <Search
                    size={13}
                    className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                  />
                  <input
                    type="text"
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    placeholder="Busca compañeros para añadir..."
                    className="w-full pl-8 pr-3 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-md placeholder-slate-400 focus:outline-none focus:bg-white focus:border-slate-300 transition-colors"
                  />
                </div>

                {/* Lista filtrada (siempre visible: muestra primeros 10 sin búsqueda) */}
                {empleadosFiltrados.length > 0 && (
                  <div className="mt-1.5 max-h-44 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-sm">
                    {loadingEmpleados ? (
                      <div className="p-3 text-xs text-slate-400 text-center">
                        Cargando empleados...
                      </div>
                    ) : empleadosFiltrados.length === 0 ? (
                      <div className="p-3 text-xs text-slate-400 text-center">
                        Sin resultados
                      </div>
                    ) : (
                      empleadosFiltrados.map((emp) => {
                        const selected = miembrosSelected.some((m) => m.id === emp.id);
                        return (
                          <button
                            key={emp.id}
                            type="button"
                            onClick={() => toggleMiembro(emp)}
                            className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left text-sm transition-colors ${
                              selected ? 'bg-cyan-50' : 'hover:bg-slate-50'
                            }`}
                          >
                            <span className="w-6 h-6 rounded-full bg-slate-200 text-slate-600 text-[10px] font-semibold flex items-center justify-center flex-shrink-0">
                              {iniciales(emp.nombre)}
                            </span>
                            <span className="flex-1 text-slate-800 truncate">{emp.nombre}</span>
                            {selected && <Check size={14} className="text-cyan-600" />}
                          </button>
                        );
                      })
                    )}
                  </div>
                )}
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-start gap-2 px-3 py-2 bg-rose-50 border border-rose-200/70 rounded-md">
                  <AlertCircle size={14} className="text-rose-500 mt-0.5 flex-shrink-0" />
                  <span className="text-xs text-rose-700">{error}</span>
                </div>
              )}
            </div>
            )}

            {paso === 2 && error && (
              <div className="mx-6 mt-3 flex items-start gap-2 px-3 py-2 bg-rose-50 border border-rose-200/70 rounded-md">
                <AlertCircle size={14} className="text-rose-500 mt-0.5 flex-shrink-0" />
                <span className="text-xs text-rose-700">{error}</span>
              </div>
            )}

            {/* Footer */}
            <div className="flex-shrink-0 flex items-center justify-end gap-2 px-6 py-3 mt-3 bg-slate-50/60 border-t border-slate-200/70">
              <button
                type="button"
                onClick={paso === 2 ? () => setPaso(1) : onClose}
                disabled={submitting}
                className="px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-md transition-colors disabled:opacity-50"
              >
                {paso === 2 ? 'Atrás' : 'Cancelar'}
              </button>
              <button
                type="submit"
                disabled={
                  submitting || !nombre.trim()
                  || (modo === 'plantilla' && (!plantillaSel || cargandoPlantilla))
                }
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 text-sm font-medium text-white bg-slate-900 hover:bg-slate-800 rounded-md transition-colors active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting
                  ? 'Creando...'
                  : modo === 'plantilla' && paso === 1
                    ? 'Revisar actividades'
                    : paso === 2
                      ? `Crear proyecto con ${activasCount} ${activasCount === 1 ? 'tarea' : 'tareas'}`
                      : 'Crear proyecto'}
              </button>
            </div>
          </form>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );

  return createPortal(modal, document.body);
};

export default NewProjectModal;
